import { CalendarClock, X } from 'lucide-react';
import { Control, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { AiCommandsList } from '@/Domain/components/Shared/AiCommandsList/AiCommandsList';
import { BoxIcon } from '@/Domain/components/Shared/BoxIcon/BoxIcon';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { characterLimit, maxWords } from '@/Domain/utils/validate';

interface GuidelinesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  control: Control<KnowledgeBase>;
  locked?: boolean;
}

export const GuidelinesSheet = ({ open, onOpenChange, control, locked }: GuidelinesSheetProps) => {
  const { t } = useTranslation(['knowledge_base', 'validation', 'common']);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-full flex-col pb-0 sm:max-w-2xl"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="flex-row items-start">
          <div className="min-w-0 flex-1">
            <SheetTitle>{t('knowledge_base:form.commands.label')}</SheetTitle>
            <SheetDescription>{t('knowledge_base:form.commands.help')}</SheetDescription>
          </div>
          <IconButton variant="secondary" className="shrink-0" onClick={() => onOpenChange(false)}>
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <div className="relative mt-2 min-h-0 flex-1 overflow-y-auto">
          <Controller
            name="ai_commands"
            control={control}
            rules={{
              validate: {
                maxWords: async (value: KnowledgeBase['ai_commands'] = []) =>
                  (await maxWords((value ?? []).join(' '), 1000)) ||
                  t('max_words', { max: 1000, ns: 'validation' }),
                characterLimit: async (value: KnowledgeBase['ai_commands'] = []) =>
                  (await characterLimit((value ?? []).join(''), 5000)) ||
                  t('character_limit', { max: 5000, ns: 'validation' }),
              },
            }}
            render={({ field: { onChange, value = [] }, fieldState: { invalid, error } }) => (
              <>
                <AiCommandsList
                  value={value}
                  onChange={onChange}
                  disabled={locked}
                  error={invalid}
                />
                <FieldError>{error?.message}</FieldError>
                {locked && (
                  <div className="group absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300 hover:bg-accent/60 hover:backdrop-blur-sm">
                    <BoxIcon variant="outlined" className="mx-auto hidden group-hover:block">
                      <CalendarClock className="mx-auto h-full text-muted-foreground" />
                    </BoxIcon>
                    <Trans
                      i18nKey="common:upgrade_package"
                      t={t}
                      components={{
                        upgrade: (
                          <Link
                            to="../subscription"
                            className="hidden text-xs font-medium text-foreground group-hover:block"
                          />
                        ),
                      }}
                    />
                  </div>
                )}
              </>
            )}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
